import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { merge } from "lodash";

import { useAppSelector } from "app/hooks";
import { primary } from "app/themes/colors";
import AutocompleteFormField from "components/autocomplete-form-field";
import CheckboxFormField from "components/checkbox-form-field";
import CPTCodeField from "components/cpt-code-field";
import CPTCodesMultiField from "components/cpt-code-field/cpt-codes-multi-field.component";
import { SECTION_SX } from "components/dialog/modal-section-styles";
import ExternalAccountField from "components/external-account-field";
import InputFormField from "components/input-form-field";
import QuickbooksProductMappingSelect from "components/quickbooks-product-mapping-select";
import SelectFormField from "components/select-form-field";
import {
  CATALOG_ENTRY_SPLIT_OFF_FEATURE,
  MULTIPLE_CPT_CODES_FEATURE,
  REPAIR_ELIGIBLE_PRODUCTS_FEATURE
} from "constants/feature-flags";
import {
  ACCESSORIES_SUB_TYPES,
  EAR_MOLD_SUBTYPE,
  SERVICES_SUB_TYPES
} from "constants/products";
import {
  ACCESSORY_PRODUCT_TYPE,
  FEE_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  PRODUCT_TYPES,
  SERVICE_PRODUCT_TYPE,
  SUPPLY_PRODUCT_TYPE
} from "constants/sales";
import { selectCurrentPractice } from "features/auth/auth-slice";
import useUserRoles from "hooks/use-user-roles.hook";
import { CPTCode } from "services/cpt-codes";
import { ProductsAccount } from "services/external-accounts";
import { MasterProduct } from "services/types/master-products.type";
import {
  type QuickBookCompany,
  type QuickBookProduct
} from "services/types/quickbooks.type";

import { TIERS_OPTIONS } from "./constants";
import MasterProductField from "./master-product-field";
import ProductPictureSection from "./product-picture-section.component";
import { ClassificationChange, getClassificationChange } from "./products.util";
import useCategoryOptions from "./use-category-options.hook";

type CatalogProductFormProps = {
  isNew?: boolean;
  isSubmitting?: boolean;
  initialValues?: Partial<ProductFormValues>;
  isEditable: boolean;
  embeddedType?: number;
  embeddedSubtype?: number | number[] | null;
  onSubmit: (data: ProductFormValues) => void;
  onCancel: () => void;
  // Enables the device-picture tile (upload is a separate multipart PATCH, so
  // it needs an existing product id — hidden on create).
  productId?: number;
  pictureUrl?: string | null;
  /**
   * Ask what re-classifying the entry to this target would do (SN-19689/19690). Supplying it is
   * what puts the "Review this change" button on the Catalog entry section; without it the form
   * behaves as it always has, which is what the inline and create forms want.
   */
  onReviewClassification?: (change: ClassificationChange) => void;
  isReviewing?: boolean;
};

export type ProductFormValues = {
  name: string;
  type: string;
  subtype: string;
  master_product: MasterProduct | null;
  price: string;
  supplier_price: string;
  component_price: string;
  max_discount: string;
  loss_and_deductible_amount: string;
  is_active: boolean;
  managed_stock: boolean;
  warranty_months: string;
  loss_damage_warranty_months: string;
  manufacturer_loss_damage_cost: string;
  cpt_code: CPTCode | null;
  cpt_codes: CPTCode[];
  taxable: boolean;
  auto_delivery: boolean;
  starred: boolean;
  repair_eligible: boolean;
  quickbooks_company: QuickBookCompany | null;
  quickbooks_product: Partial<QuickBookProduct> | null;
  tier: string | null;
  product_account?: ProductsAccount | null;
  // A ProductCategory id; "" is uncategorized (SelectFormField clears to "").
  category: number | "";
};

const EMPTY_DEFAULT_VALUES: ProductFormValues = {
  master_product: null,
  name: "",
  price: "0",
  type: "",
  subtype: "",
  supplier_price: "0",
  component_price: "0",
  max_discount: "100",
  loss_and_deductible_amount: "0",
  is_active: true,
  managed_stock: false,
  warranty_months: "",
  loss_damage_warranty_months: "",
  manufacturer_loss_damage_cost: "",
  cpt_code: null,
  cpt_codes: [],
  taxable: false,
  auto_delivery: false,
  starred: false,
  repair_eligible: false,
  quickbooks_company: null,
  quickbooks_product: null,
  tier: null,
  product_account: null,
  category: ""
};

const getSubTypeOptions = (
  type: number
): { value: string; label: string }[] => {
  if (type === ACCESSORY_PRODUCT_TYPE) {
    return ACCESSORIES_SUB_TYPES.getOptions();
  } else if (type === SERVICE_PRODUCT_TYPE) {
    return SERVICES_SUB_TYPES.getOptions();
  }

  return [];
};

// SN-19689 AC 2. Type and Sub Type describe the shared catalog entry; everything below
// describes only this practice's product. Nothing on the screen said so, which is how a user
// came to re-classify a record other practices sell by editing what looked like their own.
//
// `framed={false}` renders the children bare - same elements, same order, no extra wrapper - so
// with the flag off the form is byte-identical to before this feature (AC 1).
function FormSection({
  framed,
  title,
  caption,
  action,
  disabled = false,
  children
}: {
  framed: boolean;
  title: string;
  caption?: string;
  /** Right-aligned control in the header row — the Catalog entry section's Edit button. */
  action?: React.ReactNode;
  /** Greys the section and takes its inputs out of the tab order. */
  disabled?: boolean;
  children: React.ReactNode;
}) {
  if (!framed) return <>{children}</>;

  return (
    <Box
      component="section"
      sx={{
        ...SECTION_SX,
        // The grids inside carry spacing={4}, whose negative margins need padding to sit in.
        p: 3,
        mb: 3
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          minHeight: 32
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body1"
            sx={{
              // Field labels are already uppercase at 700 (themes/components/input.ts), so an
              // eyebrow-sized heading would read as one more field label. Larger, and in the
              // brand green, so it reads as the thing that owns the fields under it.
              color: primary[500],
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em"
            }}
          >
            {title}
          </Typography>
          {caption && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 0.5, maxWidth: "40rem" }}
            >
              {caption}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
      {/* A real <fieldset disabled> rather than pointer-events: it disables the controls
          inside, so they leave the tab order too. The reset strips the browser's chrome so
          the layout is unchanged. */}
      <Box
        component="fieldset"
        disabled={disabled}
        sx={{
          border: 0,
          m: 0,
          p: 0,
          minWidth: 0,
          mt: 3,
          opacity: disabled ? 0.5 : 1
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default function ProductForm({
  isNew,
  isSubmitting,
  isEditable,
  initialValues = EMPTY_DEFAULT_VALUES,
  embeddedType,
  embeddedSubtype,
  onSubmit,
  onCancel,
  productId,
  pictureUrl,
  onReviewClassification,
  isReviewing = false
}: CatalogProductFormProps) {
  const flags = useFlags();
  const splitOffFeature = Boolean(flags[CATALOG_ENTRY_SPLIT_OFF_FEATURE]);
  const multipleCptCodesFeature = Boolean(flags[MULTIPLE_CPT_CODES_FEATURE]);
  const repairEligibleProductsEnabled = Boolean(
    flags[REPAIR_ELIGIBLE_PRODUCTS_FEATURE]
  );
  const { canViewCosts, canAccessQBO } = useUserRoles();
  const currentPractice = useAppSelector(selectCurrentPractice);
  const { showCategory, options: categoryOptions } = useCategoryOptions(
    initialValues?.category
  );

  const quickbooksIsAvailable =
    currentPractice?.is_quickbooks_enabled && canAccessQBO;

  const defaultValues: ProductFormValues = useMemo(() => {
    return merge({}, EMPTY_DEFAULT_VALUES, initialValues);
  }, [initialValues]);

  const formMethods = useForm<ProductFormValues>({
    mode: "onChange",
    defaultValues
  });
  const {
    watch,
    setValue,
    getValues,
    control,
    reset,
    handleSubmit,
    formState: { isValid }
  } = formMethods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const [
    type,
    subtype,
    masterProduct,
    cpt_code,
    quickbooks_company,
    quickbooks_product
  ] = watch<
    [
      "type",
      "subtype",
      "master_product",
      "cpt_code",
      "quickbooks_company",
      "quickbooks_product"
    ]
  >([
    "type",
    "subtype",
    "master_product",
    "cpt_code",
    "quickbooks_company",
    "quickbooks_product"
  ]);

  const isEarmold =
    Number(type) === ACCESSORY_PRODUCT_TYPE &&
    Number(subtype) === EAR_MOLD_SUBTYPE;

  const showsTier = Number(type) === HEARING_AID_PRODUCT_TYPE;

  const entryType = defaultValues.master_product?.type ?? null;
  const entrySubtype = defaultValues.master_product?.subtype ?? null;

  // Locked until the user deliberately opens it. Two jobs: it says "this describes a shared
  // record" before anything is touched, and it keeps the classification and the pricing from
  // being edited in one breath -- a re-classification is a separate request that carries none
  // of the product's other fields.
  const [catalogOpen, setCatalogOpen] = useState(false);
  const catalogLockable = splitOffFeature && Boolean(onReviewClassification);
  // `<fieldset disabled>` only reaches form-associated elements. MUI's Select and
  // Autocomplete render a clickable `<div role="combobox">` beside a hidden input, so the
  // fieldset greys them while they stay fully operable -- the lock has to reach each field.
  const catalogLocked = catalogLockable && !catalogOpen;

  const closeCatalogSection = useCallback(() => {
    setValue("type", entryType === null ? "" : String(entryType));
    setValue("subtype", entrySubtype === null ? "" : String(entrySubtype));
    setValue("master_product", defaultValues.master_product ?? null);
    setCatalogOpen(false);
  }, [setValue, entryType, entrySubtype, defaultValues.master_product]);

  // Null unless Type or Sub Type now differ from the entry the product is actually on. Picking
  // a DIFFERENT entry is a relink and stays null — the ordinary save writes that.
  const classificationChange = splitOffFeature
    ? getClassificationChange(
        { type, subtype, master_product: masterProduct },
        defaultValues.master_product
      )
    : null;

  const subTypeOptions = useMemo(() => {
    if (
      !embeddedSubtype ||
      (Array.isArray(embeddedSubtype) && !embeddedSubtype.length)
    ) {
      return getSubTypeOptions(Number(type));
    }

    const limitToSubTypesSet = new Set<number>(
      Array.isArray(embeddedSubtype) ? embeddedSubtype : [embeddedSubtype]
    );
    return getSubTypeOptions(Number(type)).filter((item) =>
      limitToSubTypesSet.has(Number(item.value))
    );
  }, [embeddedSubtype, type]);

  useEffect(() => {
    if (
      [
        HEARING_AID_PRODUCT_TYPE,
        FEE_PRODUCT_TYPE,
        SUPPLY_PRODUCT_TYPE
      ].includes(Number(type))
    ) {
      setValue("subtype", "");
    }

    if (Number(type) !== HEARING_AID_PRODUCT_TYPE && !isEarmold) {
      setValue("warranty_months", "");
      setValue("loss_damage_warranty_months", "");
    }

    if (Number(type) !== HEARING_AID_PRODUCT_TYPE) {
      setValue("manufacturer_loss_damage_cost", "");
      setValue("tier", null);
    }

    if (Number(type) === FEE_PRODUCT_TYPE) {
      setValue("cpt_code", null);
      setValue("cpt_codes", []);
    }

    if (
      isNew &&
      [SERVICE_PRODUCT_TYPE, FEE_PRODUCT_TYPE].includes(Number(type))
    ) {
      setValue("auto_delivery", true);
    } else if (isNew) {
      setValue("auto_delivery", false);
    }
  }, [isEarmold, isNew, setValue, type]);

  useEffect(() => {
    // SN-19689: with the flag on, Type and Sub Type DESCRIBE the catalog entry rather than
    // filter for one, so clearing the picker would empty the very record being re-classified —
    // and the effect below would then wipe the pricing along with it. Flag off keeps today's
    // filter behaviour byte-for-byte.
    if (splitOffFeature) return;

    if (
      masterProduct?.type !== Number(type) ||
      (masterProduct?.type === ACCESSORY_PRODUCT_TYPE &&
        Boolean(subtype) &&
        masterProduct?.subtype !== Number(subtype))
    ) {
      setValue("master_product", null);
    } else if (
      masterProduct?.type === ACCESSORY_PRODUCT_TYPE &&
      !Boolean(subtype) &&
      masterProduct?.subtype !== null
    ) {
      setValue("subtype", masterProduct?.subtype.toString());
    }
  }, [type, subtype, masterProduct, setValue, splitOffFeature]);

  useEffect(() => {
    // `getValues`, not the watched value: watched values lag a render behind `reset`, so when
    // `defaultValues` changes underneath the form — landing on the replacement product after a
    // forward-only correction — the stale watched entry did not match the new defaults and this
    // wiped Name, price and costs on a product that had just been created with them.
    if (getValues("master_product")?.id !== defaultValues.master_product?.id) {
      setValue("name", "");
      setValue("price", "0");
      setValue("supplier_price", "0");
      setValue("component_price", "0");
      setValue("max_discount", "100");
      setValue("loss_and_deductible_amount", "0");
      setValue("cpt_code", masterProduct?.cpt_code ?? null);
      if (multipleCptCodesFeature) {
        // Filter out the primary id — cpt_codes is "additional only" per the
        // documented contract (see products.util.ts).
        const primaryId = masterProduct?.cpt_code?.id;
        setValue(
          "cpt_codes",
          (masterProduct?.cpt_codes ?? []).filter((c) => c.id !== primaryId)
        );
      }
    }
  }, [
    setValue,
    getValues,
    masterProduct,
    defaultValues.master_product,
    multipleCptCodesFeature
  ]);

  const disableTypeSelection = Boolean(embeddedType);
  const disableSubTypeSelection = Boolean(
    embeddedSubtype && !Array.isArray(embeddedSubtype)
  );

  const handleQuickbooksChange = useCallback(
    (
      quickBooksCompany: QuickBookCompany | null,
      quickBooksProduct: Partial<QuickBookProduct> | null
    ) => {
      setValue("quickbooks_company", quickBooksCompany);
      setValue("quickbooks_product", quickBooksProduct);
    },
    [setValue]
  );

  const handleSelected = useCallback(
    (product: MasterProduct) => {
      setValue("type", String(product.type));
      setValue("subtype", String(product.subtype));
    },
    [setValue]
  );

  const newProduct = useMemo(
    () => ({
      type,
      subtype,
      cpt_code
    }),
    [cpt_code, subtype, type]
  );

  const nameField = (
    <InputFormField
      name="name"
      label="Name"
      control={control}
      sx={{ ml: 0 }}
      rules={{
        maxLength: {
          value: 512,
          message:
            "The Name field cannot exceed 512 characters. Please shorten your input."
        }
      }}
      disabled={!isEditable}
    />
  );
  return (
    <FormProvider {...formMethods}>
      <FormSection
        framed={splitOffFeature}
        title="Catalog entry"
        caption={
          catalogOpen
            ? "Changing Type or Sub Type changes it for every product on this entry, not just this one."
            : undefined
        }
        disabled={catalogLockable && !catalogOpen}
        action={
          !catalogLockable ? null : catalogOpen ? (
            <Button size="small" onClick={closeCatalogSection}>
              Cancel
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              onClick={() => setCatalogOpen(true)}
              disabled={!isEditable}
              data-testid="catalog-entry-edit"
            >
              Edit
            </Button>
          )
        }
      >
        <Grid item container xs={12} spacing={4} sx={{ mb: 5 }}>
          <Grid item xs={2}>
            <SelectFormField
              name="type"
              label="Type"
              control={control}
              options={PRODUCT_TYPES.getOptions()}
              required
              afterUpdate={(value) => {
                if (Number(value) === HEARING_AID_PRODUCT_TYPE) {
                  setValue("managed_stock", true);
                }

                setValue("quickbooks_company", null);
                setValue("quickbooks_product", null);
              }}
              disabled={!isEditable || disableTypeSelection || catalogLocked}
            />
          </Grid>
          <Grid item xs={2}>
            {Boolean(subTypeOptions?.length) && (
              <SelectFormField
                name="subtype"
                label="Sub Type"
                control={control}
                options={subTypeOptions}
                required
                disabled={
                  !isEditable || disableSubTypeSelection || catalogLocked
                }
                dropDownZIndex={1502}
              />
            )}
          </Grid>
          <Grid item xs={8}>
            {Boolean(type) && (
              <MasterProductField
                required
                control={control}
                name="master_product"
                label={splitOffFeature ? "Catalog Entry" : "Product"}
                placeholder="Search product by description"
                type={Number(type) ?? 0}
                subtype={Number(subtype) || undefined}
                allowProductAddition={true}
                newProduct={newProduct}
                onSelected={handleSelected}
                disabled={!isEditable || catalogLocked}
                disableTypeSelection={disableTypeSelection}
                disableSubTypeSelection={disableSubTypeSelection}
              />
            )}
          </Grid>
        </Grid>
        {/* Deliberately a button rather than a reaction to the dropdowns. Working out what a
            re-classification would touch is a broad query across invoices, stock, orders and
            bundles, and firing it on every change would put that behind a dropdown — twice
            over, since changing Type re-filters Sub Type and fires again. Nothing is asked of
            the server until the user says this is what they meant. */}
        {classificationChange && onReviewClassification && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Button
              variant="contained"
              size="small"
              disabled={isReviewing || !isEditable}
              onClick={() => onReviewClassification(classificationChange)}
              data-testid="review-classification-change"
            >
              {isReviewing ? "Checking\u2026" : "Review this change"}
            </Button>
          </Stack>
        )}
      </FormSection>
      <FormSection
        framed={splitOffFeature}
        title="Product"
        caption={
          classificationChange
            ? "Finish with the catalog entry above first."
            : "Pricing, codes and settings for this product only."
        }
        disabled={Boolean(classificationChange)}
      >
        {showCategory && (
          <Grid item container xs={12} spacing={4} sx={{ mb: 5 }}>
            <Grid item xs={9}>
              {nameField}
            </Grid>
            <Grid item xs={3}>
              <SelectFormField
                name="category"
                label="Category"
                control={control}
                options={categoryOptions}
                disabled={!isEditable}
                dropDownZIndex={1502}
              />
            </Grid>
          </Grid>
        )}
        <Grid item container xs={12} spacing={4} sx={{ mb: 5 }}>
          <Grid item xs={1}>
            <FormControlLabel
              label="Active"
              control={
                <CheckboxFormField
                  name="is_active"
                  control={control}
                  testId="is-active"
                  disabled={!isEditable}
                />
              }
            />
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              label="Managed Stock"
              control={
                <CheckboxFormField
                  name="managed_stock"
                  control={control}
                  testId="managed-stock"
                  disabled={!isEditable}
                />
              }
            />
          </Grid>
          <Grid item xs={1}>
            <FormControlLabel
              label="Taxable"
              control={
                <CheckboxFormField
                  name="taxable"
                  control={control}
                  testId="taxable"
                  disabled={!isEditable}
                />
              }
            />
          </Grid>
          <Grid item xs={2}>
            <FormControlLabel
              label="Auto Delivered"
              control={
                <CheckboxFormField
                  name="auto_delivery"
                  control={control}
                  testId="auto_delivery"
                  disabled={!isEditable}
                />
              }
            />
          </Grid>
          {repairEligibleProductsEnabled &&
            [ACCESSORY_PRODUCT_TYPE, SUPPLY_PRODUCT_TYPE].includes(
              Number(type)
            ) && (
              <Grid item xs={2}>
                <FormControlLabel
                  label="Repair Eligible"
                  control={
                    <CheckboxFormField
                      name="repair_eligible"
                      control={control}
                      testId="repair_eligible"
                      disabled={!isEditable}
                    />
                  }
                />
              </Grid>
            )}
          {!showCategory && (
            <Grid
              item
              xs={
                repairEligibleProductsEnabled &&
                [ACCESSORY_PRODUCT_TYPE, SUPPLY_PRODUCT_TYPE].includes(
                  Number(type)
                )
                  ? 4
                  : 6
              }
            >
              {nameField}
            </Grid>
          )}
        </Grid>
        <Grid item container xs={12} spacing={4} sx={{ mb: 5 }}>
          <Grid item xs={splitOffFeature && !showsTier ? 6 : 3}>
            <CPTCodeField
              name="cpt_code"
              label={
                multipleCptCodesFeature
                  ? "Primary CPT or HCPCS Code"
                  : "CPT or HCPCS Code"
              }
              control={control}
              disabled={!isEditable}
            />
          </Grid>
          {/* Flag off restores the original shape exactly: the column is always laid out and
              only the FIELD inside it is conditional — which is what leaves a dead quarter-width
              gap on a non-hearing-aid product. The flag is what closes that gap. */}
          {(!splitOffFeature || showsTier) && (
            <Grid item xs={3}>
              {showsTier && (
                <AutocompleteFormField
                  openOnFocus
                  name="tier"
                  label="Tier"
                  options={TIERS_OPTIONS}
                  getOptionLabel={(value: number) => `Tier ${value}`}
                  popupIcon={<ArrowDropDownIcon />}
                  control={control}
                  sx={{}}
                />
              )}
            </Grid>
          )}
          <Grid item xs={6}>
            <ExternalAccountField
              name="product_account"
              control={control}
              disabled={!isEditable}
            />
          </Grid>
        </Grid>
        {multipleCptCodesFeature && (
          <Grid item container xs={12} spacing={4} sx={{ mb: 5 }}>
            <Grid item xs={6}>
              <CPTCodesMultiField
                name="cpt_codes"
                label="Additional CPT or HCPCS Codes"
                control={control}
                disabled={!isEditable}
                excludeIds={cpt_code?.id ? [cpt_code.id] : undefined}
              />
            </Grid>
          </Grid>
        )}
        <Grid item container xs={12} spacing={4} sx={{ mb: 5 }}>
          <Grid item xs={3}>
            <InputFormField
              name="price"
              label="Retail Price"
              autoComplete="off"
              isMoney
              startAdornment="$"
              control={control}
              required
              rules={{
                min: { value: 0, message: "value can't be less than zero" }
              }}
              sx={{ ml: 0 }}
              disabled={!isEditable}
            />
          </Grid>
          {Boolean(type) && Number(type) === ACCESSORY_PRODUCT_TYPE && (
            <Grid item xs={3}>
              <InputFormField
                name="component_price"
                label="With Hearing Aid Price"
                autoComplete="off"
                isMoney
                startAdornment="$"
                control={control}
                required
                rules={{
                  min: { value: 0, message: "value can't be less than zero" }
                }}
                sx={{ ml: 0 }}
                disabled={!isEditable}
              />
            </Grid>
          )}
          {Boolean(type) &&
            Number(type) !== SERVICE_PRODUCT_TYPE &&
            canViewCosts && (
              <Grid item xs={3}>
                <InputFormField
                  name="supplier_price"
                  label="Manufacturer Cost"
                  autoComplete="off"
                  isMoney
                  startAdornment="$"
                  control={control}
                  required
                  rules={{
                    min: { value: 0, message: "value can't be less than zero" }
                  }}
                  sx={{ ml: 0 }}
                  disabled={!isEditable}
                />
              </Grid>
            )}
          <Grid item xs={3}>
            <InputFormField
              name="max_discount"
              label="Max Discount"
              autoComplete="off"
              type="number"
              inputProps={{ min: 0, max: 100 }}
              startAdornment="%"
              control={control}
              required
              rules={{
                min: { value: 0, message: "value can't be less than zero" },
                max: { value: 100, message: "value can't be greater than 100" }
              }}
              sx={{ ml: 0 }}
              disabled={!isEditable}
            />
          </Grid>
        </Grid>

        {isEarmold && (
          <Grid item container xs={12} spacing={4} sx={{ mb: 5 }}>
            <Grid item xs={3}>
              <InputFormField
                name="warranty_months"
                label="Warranty Length (Months)"
                type="number"
                inputProps={{ min: 0 }}
                control={control}
                rules={{
                  min: { value: 0, message: "value can't be less than zero" }
                }}
                sx={{ ml: 0 }}
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={3}>
              <InputFormField
                name="loss_damage_warranty_months"
                label="L&D Warranty Length (Months)"
                type="number"
                inputProps={{ min: 0 }}
                control={control}
                rules={{
                  min: { value: 0, message: "value can't be less than zero" }
                }}
                sx={{ ml: 0 }}
                disabled={!isEditable}
              />
            </Grid>
          </Grid>
        )}

        {Number(type) === HEARING_AID_PRODUCT_TYPE && (
          <Grid item container xs={12} spacing={4} sx={{ mb: 5 }}>
            <Grid item xs={3}>
              <InputFormField
                name="warranty_months"
                label="Warranty Length (Months)"
                type="number"
                inputProps={{ min: 0 }}
                control={control}
                rules={{
                  min: { value: 0, message: "value can't be less than zero" }
                }}
                sx={{ ml: 0 }}
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={3}>
              <InputFormField
                name="loss_damage_warranty_months"
                label="L&D Warranty Length (Months)"
                type="number"
                inputProps={{ min: 0 }}
                control={control}
                rules={{
                  min: { value: 0, message: "value can't be less than zero" }
                }}
                sx={{ ml: 0 }}
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={3}>
              <InputFormField
                name="manufacturer_loss_damage_cost"
                label="Manufacturer L&D Replacement Cost"
                type="number"
                autoComplete="off"
                isMoney
                inputProps={{ min: 0 }}
                startAdornment="$"
                control={control}
                rules={{
                  min: { value: 0, message: "value can't be less than zero" }
                }}
                sx={{ ml: 0 }}
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={3}>
              <InputFormField
                name="loss_and_deductible_amount"
                label="L&D Deductible Amount"
                autoComplete="off"
                isMoney
                startAdornment="$"
                control={control}
                rules={{
                  min: { value: 0, message: "value can't be less than zero" }
                }}
                sx={{ ml: 0 }}
                disabled={!isEditable}
              />
            </Grid>
          </Grid>
        )}

        {Boolean(productId) &&
          [
            HEARING_AID_PRODUCT_TYPE,
            ACCESSORY_PRODUCT_TYPE,
            SUPPLY_PRODUCT_TYPE
          ].includes(Number(type)) && (
            <ProductPictureSection
              productId={Number(productId)}
              pictureUrl={pictureUrl}
              isEditable={isEditable}
            />
          )}

        {quickbooksIsAvailable && (
          <QuickbooksProductMappingSelect
            quickBooksCompany={quickbooks_company}
            quickBooksProduct={quickbooks_product}
            onChange={handleQuickbooksChange}
          />
        )}
      </FormSection>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{ mr: 2, minWidth: "7rem" }}
        >
          {!isEditable ? "Close" : "Cancel"}
        </Button>
        {isEditable && (
          <LoadingButton
            onClick={handleSubmit(onSubmit)}
            loading={isSubmitting}
            type="button"
            variant="contained"
            disabled={!isValid}
            sx={{ minWidth: "7rem" }}
          >
            Save
          </LoadingButton>
        )}
      </Box>
    </FormProvider>
  );
}
